import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import {
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Image as ImageIcon,
  Upload,
  Check,
  LoaderCircle,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

/**
 * Props:
 * isOpen          - Whether the modal is visible
 * imageFile       - Original image File selected by the user
 * onClose         - Called when the modal is closed/cancelled
 * onCropComplete  - Called with the compressed File
 *
 * Optional:
 * aspect          - Crop aspect ratio (default: 1)
 * cropShape       - "round" or "rect" (default: "round")
 * maxSizeKB       - Target maximum output size in KB (default: 300)
 * outputSize      - Maximum output dimension in pixels (default: 800)
 */
export default function ProfilePictureCropperModal({
  isOpen,
  imageFile,
  onClose,
  onCropComplete,
  aspect = 1,
  cropShape = "round",
  maxSizeKB = 300,
  outputSize = 800,
}) {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [originalSize, setOriginalSize] = useState(0);

  useEffect(() => {
    if (!isOpen || !imageFile) {
      setImageSrc("");
      return;
    }

    const url = URL.createObjectURL(imageFile);

    setImageSrc(url);
    setOriginalSize(imageFile.size);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError("");

    return () => URL.revokeObjectURL(url);
  }, [isOpen, imageFile]);

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const getCroppedImage = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) {
      throw new Error("Please adjust your image before saving.");
    }

    const image = new Image();
    image.src = imageSrc;

    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error("Unable to load image."));
    });

    const { x, y, width, height } = croppedAreaPixels;

    const scale = Math.min(1, outputSize / Math.max(width, height));
    const canvas = document.createElement("canvas");

    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Your browser could not process this image.");
    }

    // JPEG has no transparency. Use white behind transparent PNGs.
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    context.drawImage(
      image,
      x,
      y,
      width,
      height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Encode the cropped image as JPEG and reduce quality until
    // the target size is reached, where possible.
    const targetBytes = maxSizeKB * 1024;
    let quality = 0.88;
    let blob = await canvasToBlob(canvas, quality);

    while (blob.size > targetBytes && quality > 0.4) {
      quality = Math.max(0.4, quality - 0.08);
      blob = await canvasToBlob(canvas, quality);
    }

    // If quality alone is not enough, resize further.
    while (blob.size > targetBytes && canvas.width > 240) {
      const nextWidth = Math.max(240, Math.round(canvas.width * 0.8));
      const nextHeight = Math.max(240, Math.round(canvas.height * 0.8));

      const smallerCanvas = document.createElement("canvas");
      smallerCanvas.width = nextWidth;
      smallerCanvas.height = nextHeight;

      const smallerContext = smallerCanvas.getContext("2d");

      if (!smallerContext) break;

      smallerContext.fillStyle = "#FFFFFF";
      smallerContext.fillRect(0, 0, nextWidth, nextHeight);
      smallerContext.imageSmoothingEnabled = true;
      smallerContext.imageSmoothingQuality = "high";
      smallerContext.drawImage(
        canvas,
        0,
        0,
        nextWidth,
        nextHeight
      );

      canvas.width = nextWidth;
      canvas.height = nextHeight;
      context.drawImage(smallerCanvas, 0, 0);

      blob = await canvasToBlob(canvas, quality);
    }

    const name =
      (imageFile?.name || "profile-picture").replace(/\.[^.]+$/, "") +
      ".jpg";

    return new File([blob], name, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  }, [imageSrc, croppedAreaPixels, imageFile, maxSizeKB, outputSize]);

  const handleSave = async () => {
    setProcessing(true);
    setError("");

    try {
      const file = await getCroppedImage();
      await onCropComplete(file);
      onClose();
    } catch (err) {
      setError(err.message || "Unable to process the image. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !processing) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, processing, onClose]);

  if (!isOpen || !imageSrc) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center
        overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !processing) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-cropper-title"
        className="my-auto flex max-h-[94vh] w-full max-w-3xl flex-col
          overflow-hidden rounded-2xl border border-slate-200 bg-white
          shadow-2xl shadow-slate-950/20"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100
          px-5 py-4 sm:px-7">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center
              rounded-xl bg-blue-50 text-blue-600">
              <ImageIcon size={22} />
            </div>

            <div>
              <h2
                id="profile-cropper-title"
                className="text-base font-bold tracking-tight text-slate-900 sm:text-lg"
              >
                Edit profile picture
              </h2>
              <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                Crop, adjust and optimize your photo
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            aria-label="Close image editor"
            className="flex h-9 w-9 items-center justify-center rounded-lg
              text-slate-500 transition hover:bg-slate-100 hover:text-slate-900
              disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Main editor */}
        <div className="overflow-y-auto p-4 sm:p-7">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_220px]">
            <div className="min-w-0">
              <div className="relative h-[280px] overflow-hidden rounded-xl
                bg-slate-950 sm:h-[360px]">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  cropShape={cropShape}
                  showGrid={false}
                  restrictPosition
                  minZoom={1}
                  maxZoom={3}
                  zoomSpeed={0.15}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                />
              </div>

              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <label
                    htmlFor="profile-picture-zoom"
                    className="text-sm font-semibold text-slate-800"
                  >
                    Zoom
                  </label>
                  <span className="rounded-md bg-white px-2 py-1 text-xs
                    font-semibold text-blue-700 ring-1 ring-slate-200">
                    {Math.round(zoom * 100)}%
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    aria-label="Zoom out"
                    onClick={() =>
                      setZoom((value) => Math.max(1, value - 0.1))
                    }
                    disabled={zoom <= 1}
                    className="flex h-9 w-9 shrink-0 items-center justify-center
                      rounded-lg border border-slate-200 bg-white text-slate-600
                      transition hover:border-blue-300 hover:text-blue-600
                      disabled:opacity-40"
                  >
                    <ZoomOut size={17} />
                  </button>

                  <input
                    id="profile-picture-zoom"
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(event) => setZoom(Number(event.target.value))}
                    aria-label="Image zoom level"
                    className="h-2 min-w-0 flex-1 cursor-pointer appearance-none
                      rounded-full bg-slate-200 accent-blue-600"
                  />

                  <button
                    type="button"
                    aria-label="Zoom in"
                    onClick={() =>
                      setZoom((value) => Math.min(3, value + 0.1))
                    }
                    disabled={zoom >= 3}
                    className="flex h-9 w-9 shrink-0 items-center justify-center
                      rounded-lg border border-slate-200 bg-white text-slate-600
                      transition hover:border-blue-300 hover:text-blue-600
                      disabled:opacity-40"
                  >
                    <ZoomIn size={17} />
                  </button>
                </div>

                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Drag the image to reposition it. Use the slider to zoom in
                  or out.
                </p>
              </div>
            </div>

            {/* Right-side information */}
            <aside className="space-y-4">
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wider
                  text-slate-500">
                  Preview
                </p>

                <div className="mt-4 flex justify-center">
                  <div className="flex h-28 w-28 items-center justify-center
                    overflow-hidden rounded-full border-4 border-white
                    bg-slate-100 shadow-lg ring-1 ring-slate-200">
                    <div className="relative h-full w-full">
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        restrictPosition
                        minZoom={1}
                        maxZoom={3}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={handleCropComplete}
                        style={{
                          containerStyle: {
                            background: "#f1f5f9",
                          },
                          cropAreaStyle: {
                            border: "none",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <p className="mt-3 text-center text-xs text-slate-500">
                  Profile photo
                </p>
              </div>

              <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                <div className="flex items-center gap-2 text-blue-800">
                  <Sparkles size={17} />
                  <span className="text-sm font-bold">Smart compression</span>
                </div>

                <ul className="mt-3 space-y-2.5 text-xs leading-5 text-slate-600">
                  <li className="flex gap-2">
                    <Check size={15} className="mt-0.5 shrink-0 text-blue-600" />
                    High-quality JPEG output
                  </li>
                  <li className="flex gap-2">
                    <Check size={15} className="mt-0.5 shrink-0 text-blue-600" />
                    Maximum {outputSize}px image dimension
                  </li>
                  <li className="flex gap-2">
                    <Check size={15} className="mt-0.5 shrink-0 text-blue-600" />
                    Target size {maxSizeKB} KB
                  </li>
                </ul>
              </div>

              <div className="flex items-start gap-2 rounded-xl bg-slate-50 p-3">
                <ShieldCheck size={17} className="mt-0.5 shrink-0 text-emerald-600" />
                <p className="text-xs leading-5 text-slate-500">
                  Your image is cropped and compressed directly in your
                  browser before being added to the form.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg
                  border border-slate-200 bg-white px-3 py-2.5 text-sm
                  font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                <RotateCcw size={15} />
                Reset adjustment
              </button>
            </aside>
          </div>

          {error && (
            <p role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3
              text-sm text-red-600">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-3 border-t border-slate-100
          bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between
          sm:px-7">
          <div className="text-xs text-slate-500">
            Original size: {(originalSize / 1024).toFixed(0)} KB
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="flex-1 rounded-lg border border-slate-200 px-5 py-2.5
                text-sm font-semibold text-slate-700 transition
                hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={processing || !croppedAreaPixels}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg
                bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white
                shadow-sm shadow-blue-600/20 transition hover:bg-blue-700
                disabled:cursor-not-allowed disabled:opacity-60
                sm:flex-none"
            >
              {processing ? (
                <>
                  <LoaderCircle size={17} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Save Photo
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Image compression failed."));
      },
      "image/jpeg",
      quality
    );
  });
}

